import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Field } from '../ui/Field';
import { PanelSection } from '../ui/PanelSection';
import { SegmentedChoice } from '../ui/SegmentedChoice';
import type { ChoiceOption } from '../ui/SegmentedChoice';
import { Sheet } from '../ui/Sheet';
import { AUTO_LOCK, PIN } from '../../../lib/rcu/catalog/security';
import { isPinGateSuccess, PIN_GATE_MESSAGE, PIN_GATE_OUTCOME } from '../../../lib/technician/catalog';
import type { PinGateOutcome } from '../../../lib/technician/catalog';
import { useTechnicianStore } from '../../../stores/technicianStore';

/** Auto-lock choices: the configured options plus "never". */
const AUTO_LOCK_OPTIONS: readonly ChoiceOption<string>[] = [
  ...AUTO_LOCK.OPTIONS_MINUTES,
  AUTO_LOCK.NEVER_MINUTES,
].map((minutes) => ({
  value: String(minutes),
  label: minutes === AUTO_LOCK.NEVER_MINUTES ? 'Never' : `${minutes} min`,
}));

const messageTone = (outcome: PinGateOutcome | null): string =>
  outcome === null ? 'text-[#666666]' : isPinGateSuccess(outcome) ? 'text-[#2E7D32]' : 'text-[#C62828]';

export interface SecuritySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Console access: how long it stays open, and the PIN that opens it. */
export const SecuritySheet: React.FC<SecuritySheetProps> = ({ isOpen, onClose }) => {
  const autoLockMinutes = useTechnicianStore((state) => state.state.autoLockMinutes);
  const setAutoLockMinutes = useTechnicianStore((state) => state.setAutoLockMinutes);
  const changePin = useTechnicianStore((state) => state.changePin);

  const [currentPin, setCurrentPin] = React.useState('');
  const [nextPin, setNextPin] = React.useState('');
  const [confirmation, setConfirmation] = React.useState('');
  const [outcome, setOutcome] = React.useState<PinGateOutcome | null>(null);

  const reset = (): void => {
    setCurrentPin('');
    setNextPin('');
    setConfirmation('');
    setOutcome(null);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const save = (): void => {
    const result = changePin(currentPin, nextPin, confirmation);
    setOutcome(result.outcome);
    if (result.outcome === PIN_GATE_OUTCOME.PIN_CHANGED) {
      setCurrentPin('');
      setNextPin('');
      setConfirmation('');
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      title="Console security"
      subtitle="Applies to this tablet only"
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          <Button variant="primary" onClick={save} disabled={nextPin === '' || confirmation === ''}>
            Change PIN
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <PanelSection title="Auto-lock" description="Locks the console when nobody is using it.">
          <SegmentedChoice
            label="Lock after"
            options={AUTO_LOCK_OPTIONS}
            value={String(autoLockMinutes)}
            onChange={(value) => setAutoLockMinutes(Number(value))}
          />
        </PanelSection>

        <PanelSection
          title="Change PIN"
          description={`${PIN.LENGTH} digits. The PIN keeps guests out of this console; it is not strong encryption.`}
        >
          <div className="space-y-3">
            <Field
              label="Current PIN"
              value={currentPin}
              onChange={setCurrentPin}
              inputMode="numeric"
              maxLength={PIN.LENGTH}
              secret
            />
            <Field
              label="New PIN"
              value={nextPin}
              onChange={setNextPin}
              inputMode="numeric"
              maxLength={PIN.LENGTH}
              secret
            />
            <Field
              label="Repeat new PIN"
              value={confirmation}
              onChange={setConfirmation}
              inputMode="numeric"
              maxLength={PIN.LENGTH}
              secret
            />
            <p className={`text-sm ${messageTone(outcome)}`}>
              {outcome === null
                ? `Forgotten PINs are reset from the gate with this RCU\u2019s device ID.`
                : PIN_GATE_MESSAGE[outcome]}
            </p>
          </div>
        </PanelSection>
      </div>
    </Sheet>
  );
};

export default SecuritySheet;
